import { Dialog, Transition } from '@headlessui/react';
import { BuildingOfficeIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Fragment, useState } from 'react';

import { selectOrganization } from '../services/organizationService';
import { isLoadingOrgs, Organization, organizations } from '../state/organization';

interface OrganizationSelectModalProps {
    isOpen: boolean;
    onSelect: () => void;
}

function OrganizationSelectModal({ isOpen, onSelect }: OrganizationSelectModalProps) {
    const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const orgs = organizations.value;
    const isLoading = isLoadingOrgs.value;

    const handleSelect = async () => {
        if (!selectedOrgId) return;

        setIsSubmitting(true);
        setError(null);

        try {
            await selectOrganization(selectedOrgId);
            onSelect();
        } catch (err) {
            setError(
                err instanceof Error ? err.message : 'Failed to select organization',
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const getRoleBadgeColor = (role?: string) => {
        switch (role?.toLowerCase()) {
            case 'admin':
            case 'owner':
                return 'bg-purple-100 text-purple-700';
            case 'member':
                return 'bg-blue-100 text-blue-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-50"
                onClose={() => {
                    // Non-dismissible - user must select an organization
                }}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-6">
                                    <div className="text-center sm:text-left">
                                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100 sm:mx-0">
                                            <BuildingOfficeIcon
                                                className="h-6 w-6 text-indigo-600"
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <Dialog.Title
                                            as="h3"
                                            className="mt-4 text-lg font-semibold leading-6 text-gray-900"
                                        >
                                            Select Organization
                                        </Dialog.Title>
                                        <p className="mt-2 text-sm text-gray-500">
                                            You belong to multiple organizations. Please
                                            select which one you would like to use.
                                        </p>
                                    </div>

                                    {isLoading ? (
                                        <div className="mt-4 flex justify-center py-8">
                                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
                                        </div>
                                    ) : orgs.length === 0 ? (
                                        <div className="mt-4 rounded-md bg-yellow-50 p-4">
                                            <p className="text-sm text-yellow-700">
                                                No organizations found. Please contact
                                                support.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="mt-4 space-y-2">
                                            {orgs.map((org: Organization) => (
                                                <button
                                                    key={org.id}
                                                    onClick={() =>
                                                        setSelectedOrgId(org.id)
                                                    }
                                                    className={`w-full flex items-center justify-between rounded-lg border-2 p-4 text-left transition-colors ${
                                                        selectedOrgId === org.id
                                                            ? 'border-indigo-500 bg-indigo-50'
                                                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
                                                            <BuildingOfficeIcon className="h-5 w-5 text-gray-600" />
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-900">
                                                                {org.name}
                                                            </p>
                                                            {org.role && (
                                                                <span
                                                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getRoleBadgeColor(org.role)}`}
                                                                >
                                                                    {org.role}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {selectedOrgId === org.id && (
                                                        <CheckIcon className="h-5 w-5 text-indigo-600" />
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {error && (
                                        <div className="mt-4 rounded-md bg-red-50 p-3">
                                            <p className="text-sm text-red-700">
                                                {error}
                                            </p>
                                        </div>
                                    )}
                                </div>

                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        type="button"
                                        disabled={!selectedOrgId || isSubmitting}
                                        onClick={handleSelect}
                                        className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2" />
                                                Selecting...
                                            </>
                                        ) : (
                                            'Continue'
                                        )}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

export default OrganizationSelectModal;
